import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TourStep } from './types';
import { TOUR_CONFIG } from './constants';

interface TooltipPosition {
  top: number;
  left: number;
  width: number;
  arrowPosition: 'top' | 'bottom' | 'left' | 'right';
  /** Posição horizontal da seta dentro do balão, sobre o centro do alvo (24/09). */
  arrowX: number;
}

// Safe margins that account for notch, status bar, and bottom nav
const MIN_TOP = 48;      // status bar + notch (Dynamic Island ~59px, safe at 48)
const MIN_BOTTOM = 100;  // bottom nav (80px) + safe area inset (~20px)
const MIN_SIDE = 16;

interface TourTooltipProps {
  step: TourStep;
  currentIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  isVisible: boolean;
}

export function TourTooltip({
  step,
  currentIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  isVisible,
}: TourTooltipProps) {
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const [attempts, setAttempts] = useState(0);
  const retryTimeoutRef = useRef<number | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const debounceTimeoutRef = useRef<number | null>(null);
  const hasScrolledRef = useRef(false);
  const scrollRafRef = useRef<number | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const primaryRef = useRef<HTMLButtonElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === totalSteps - 1;

  // Reset scroll flag when step changes
  useEffect(() => {
    hasScrolledRef.current = false;
  }, [step.id]);

  const updatePosition = useCallback(() => {
    const element = document.querySelector(step.targetSelector);
    if (!element) {
      if (attempts < TOUR_CONFIG.MAX_RETRY_ATTEMPTS) {
        retryTimeoutRef.current = window.setTimeout(() => {
          setAttempts(prev => prev + 1);
        }, TOUR_CONFIG.RETRY_DELAY_MS);
      }
      return;
    }

    const bounds = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Responsive width: 320px max, but shrink on narrow screens
    const tooltipWidth = Math.min(320, viewportWidth - MIN_SIDE * 2);
    // Altura medida do balão (24/09: a estimativa de 180px contra ~213 reais fazia o
    // balão cobrir 13px do alvo). Na primeira medição ainda não existe: usa a estimativa.
    const tooltipHeight = tooltipRef.current?.offsetHeight || 180;
    const offset = 12;
    const padding = step.spotlightPadding || 8;

    let top = 0;
    let left = 0;
    let arrowPosition = step.position;

    switch (step.position) {
      // `arrowPosition` é o lado do BALÃO onde a seta fica: balão abaixo do alvo = seta
      // em cima, apontando para ele. Antes herdava `step.position` e todo passo
      // 'bottom' desenhava a seta embaixo, apontando para o bloco seguinte (24/09).
      case 'bottom':
        top = bounds.bottom + padding + offset;
        left = bounds.left + bounds.width / 2 - tooltipWidth / 2;
        arrowPosition = 'top';
        break;
      case 'top':
        top = bounds.top - padding - offset - tooltipHeight;
        left = bounds.left + bounds.width / 2 - tooltipWidth / 2;
        arrowPosition = 'bottom';
        break;
      case 'left':
      case 'right':
        // On mobile, left/right don't make sense — fall through to bottom
        top = bounds.bottom + padding + offset;
        left = bounds.left + bounds.width / 2 - tooltipWidth / 2;
        arrowPosition = 'top';
        break;
    }

    // Clamp horizontal position
    if (left < MIN_SIDE) left = MIN_SIDE;
    if (left + tooltipWidth > viewportWidth - MIN_SIDE) left = viewportWidth - tooltipWidth - MIN_SIDE;

    // Clamp vertical position — flip if needed
    if (top < MIN_TOP) {
      top = bounds.bottom + padding + offset;
      arrowPosition = 'top';
    }
    if (top + tooltipHeight > viewportHeight - MIN_BOTTOM) {
      top = bounds.top - padding - offset - tooltipHeight;
      arrowPosition = 'bottom';
    }
    // Final clamp: never go above MIN_TOP even after flip
    if (top < MIN_TOP) top = MIN_TOP;

    // Only scroll once per step
    if (!hasScrolledRef.current) {
      // 'center', não 'nearest': 'nearest' encostava o alvo no pé da tela, embaixo da barra
      // de navegação, e sem espaço para o balão (medido 24/09 na home nova).
      element.scrollIntoView({ block: 'center', behavior: 'smooth' });
      hasScrolledRef.current = true;
    }

    // A seta fica sobre o centro do alvo, não no meio do balão: quando o balão é
    // empurrado para caber na tela, "50%" apontava para o vazio (passo 5, 24/09).
    const arrowX = Math.min(Math.max(bounds.left + bounds.width / 2 - left, 24), tooltipWidth - 24);
    setPosition({ top, left, width: tooltipWidth, arrowPosition, arrowX });
    setAttempts(0);
  }, [step, attempts]);

  // Debounced scroll handler using rAF (prevents jank on iOS momentum scroll)
  const handleScroll = useCallback(() => {
    if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = requestAnimationFrame(updatePosition);
  }, [updatePosition]);

  const debouncedUpdatePosition = useMemo(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = window.setTimeout(updatePosition, TOUR_CONFIG.MUTATION_DEBOUNCE_MS);
    };
  }, [updatePosition]);

  useEffect(() => {
    if (!isVisible) {
      setPosition(null);
      setAttempts(0);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      return;
    }

    updatePosition();

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', updatePosition);

    observerRef.current = new MutationObserver(debouncedUpdatePosition);
    observerRef.current.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
      }
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [step, isVisible, attempts, updatePosition, handleScroll, debouncedUpdatePosition]);

  const getArrowStyles = () => {
    if (!position) return {};

    const arrowSize = 8;
    const base = {
      position: 'absolute' as const,
      width: 0,
      height: 0,
      borderStyle: 'solid',
    };

    switch (position.arrowPosition) {
      case 'top':
        return {
          ...base,
          top: -arrowSize,
          left: position.arrowX,
          transform: 'translateX(-50%)',
          borderWidth: `0 ${arrowSize}px ${arrowSize}px ${arrowSize}px`,
          borderColor: 'transparent transparent hsl(var(--card)) transparent',
        };
      case 'bottom':
        return {
          ...base,
          bottom: -arrowSize,
          left: position.arrowX,
          transform: 'translateX(-50%)',
          borderWidth: `${arrowSize}px ${arrowSize}px 0 ${arrowSize}px`,
          borderColor: 'hsl(var(--card)) transparent transparent transparent',
        };
      case 'left':
        return {
          ...base,
          left: -arrowSize,
          top: '50%',
          transform: 'translateY(-50%)',
          borderWidth: `${arrowSize}px ${arrowSize}px ${arrowSize}px 0`,
          borderColor: 'transparent hsl(var(--card)) transparent transparent',
        };
      case 'right':
        return {
          ...base,
          right: -arrowSize,
          top: '50%',
          transform: 'translateY(-50%)',
          borderWidth: `${arrowSize}px 0 ${arrowSize}px ${arrowSize}px`,
          borderColor: 'transparent transparent transparent hsl(var(--card))',
        };
      default:
        return {};
    }
  };

  // Diálogo acessível (auditoria do Astra, 24/09): o tour cobre a página, então o foco
  // mora nele enquanto aberto e volta para onde estava quando fecha.
  useEffect(() => {
    if (isVisible) {
      if (!returnFocusRef.current) returnFocusRef.current = document.activeElement as HTMLElement | null;
    } else if (returnFocusRef.current) {
      returnFocusRef.current.focus?.();
      returnFocusRef.current = null;
    }
  }, [isVisible]);

  const hasPosition = !!position;
  useEffect(() => {
    if (isVisible && hasPosition) primaryRef.current?.focus({ preventScroll: true });
  }, [isVisible, hasPosition, step.id]);

  const onDialogKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onSkip();
      return;
    }
    if (e.key !== 'Tab' || !tooltipRef.current) return;
    const focusables = [...tooltipRef.current.querySelectorAll<HTMLElement>('button:not([disabled])')];
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  // Fallback UI when element not found - always visible so user can exit
  const showFallback = isVisible && !position && attempts >= TOUR_CONFIG.MAX_RETRY_ATTEMPTS;
  const showLoading = isVisible && !position && attempts < TOUR_CONFIG.MAX_RETRY_ATTEMPTS && attempts > 3;

  return (
    <AnimatePresence>
      {/* Loading state - show after a few attempts */}
      {showLoading && (
        <motion.div
          initial={false}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed z-[10003] bg-card border border-border rounded-xl shadow-xl px-4 py-3 flex items-center gap-3 pointer-events-auto"
          style={{
            bottom: MIN_BOTTOM,
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
          <span className="text-[15px] text-muted-foreground">Loading tour...</span>
          <Button variant="ghost" onClick={onSkip} className="min-h-12 min-w-12 px-3 text-[15px]">
            Exit
          </Button>
        </motion.div>
      )}

      {/* Fallback when element never found */}
      {showFallback && (
        <motion.div
          initial={{ y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="fixed z-[10003] bg-card border border-border rounded-2xl shadow-xl p-5 pointer-events-auto"
          style={{
            bottom: MIN_BOTTOM,
            left: '50%',
            transform: 'translateX(-50%)',
            width: Math.min(320, typeof window !== 'undefined' ? window.innerWidth - 32 : 320),
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <p className="text-[16px] text-muted-foreground mb-4">
            Unable to find the tour target. The element may not be visible on this screen.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onNext} className="flex-1 min-h-12 min-w-12 text-[15px]">
              Skip Step
            </Button>
            <Button variant="default" onClick={onSkip} className="flex-1 min-h-12 min-w-12 text-[15px]">
              Exit Tour
            </Button>
          </div>
        </motion.div>
      )}

      {/* Normal tooltip */}
      {isVisible && position && (
        <motion.div
          // Nasce opaco: com a janela sem foco o navegador segura os quadros, e um balão
          // com botões preso em opacidade 0,3 é ilegível (medido 24/09). Só transform anima.
          ref={tooltipRef}
          initial={{ y: 10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="tour-step-title"
          aria-describedby="tour-step-description"
          onKeyDown={onDialogKeyDown}
          className="fixed z-[10003] bg-card border border-border rounded-2xl shadow-xl p-5 pointer-events-auto"
          style={{
            top: position.top,
            left: position.left,
            width: position.width,
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
          {/* Arrow */}
          <div style={getArrowStyles()} />

          {/* Fechar — 48px (24/09; era 44) */}
          <button
            onClick={onSkip}
            className="absolute top-1 right-1 size-12 shrink-0 rounded-full hover:bg-muted transition-colors flex items-center justify-center"
            aria-label="Skip tour"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>

          {/* Content */}
          <div className="pr-10">
            <h3 id="tour-step-title" className="text-[17px] font-bold text-foreground mb-1.5">{step.title}</h3>
            <p id="tour-step-description" className="text-[16px] text-muted-foreground leading-relaxed">{step.description}</p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
            {/* Progresso: "1/5" basta (as bolinhas saíram para os botões caberem com 48px). */}
            <span className="text-[15px] text-muted-foreground font-medium">
              {currentIndex + 1}/{totalSteps}
            </span>

            {/* Navegação — 48px */}
            <div className="flex items-center gap-1.5">
              {!isFirst && (
                <Button
                  variant="ghost"
                  onClick={onPrev}
                  className="min-h-12 min-w-12 px-3 text-[15px]"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
              <Button
                ref={primaryRef}
                onClick={onNext}
                className="min-h-12 min-w-12 px-4 text-[15px]"
              >
                {isLast ? 'Finish' : 'Next'}
                {!isLast && <ChevronRight className="w-4 h-4 ml-1" />}
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
